/**
 * MonedaSelector
 * ─────────────────────────────────────────────────────────────────────────────
 * Selector de moneda + tipo de cambio (siempre visible para CRC y USD).
 *
 * Props:
 *   moneda          → 'CRC' | 'USD'
 *   onMonedaChange  → callback al cambiar moneda
 *   tipoCambio      → número (TC actual)
 *   onTCChange      → callback al editar el TC (solo USD)
 *   loadingTC       → boolean
 *   tcFuente        → 'BCCR' | 'MANUAL' | 'FALLBACK'
 *   monto           → número (el monto original en la moneda seleccionada)
 *   compact         → boolean (versión sin etiqueta larga)
 *   disabled        → boolean
 */

import React from "react";

const BADGE = {
  BCCR:     { color: "#4ade80", text: "BCCR" },
  MANUAL:   { color: "#fb923c", text: "Manual" },
  FALLBACK: { color: "#f87171", text: "Sin TC — ingresalo" },
};

const fmtCRC = (n) =>
  new Intl.NumberFormat("es-CR", {
    style: "currency", currency: "CRC", maximumFractionDigits: 2,
  }).format(n);

const fmtUSD = (n) =>
  new Intl.NumberFormat("es-CR", {
    style: "currency", currency: "USD", maximumFractionDigits: 2,
  }).format(n);

export function MonedaSelector({
  moneda        = "CRC",
  onMonedaChange,
  tipoCambio    = 1,
  onTCChange,
  loadingTC     = false,
  tcFuente      = "BCCR",
  monto         = 0,
  compact       = false,
  disabled      = false,
}) {
  const badge      = BADGE[tcFuente] || BADGE.BCCR;
  const tc         = parseFloat(tipoCambio || 1);
  const montoNum   = parseFloat(monto || 0);

  // Equivalente siempre en colones
  const equivalente = moneda === "USD" ? montoNum * tc : montoNum;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>

      {/* ── Segmented control CRC / USD ───────────────────────────── */}
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        {!compact && (
          <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginRight: 6 }}>
            Moneda:
          </span>
        )}
        {["CRC", "USD"].map((m) => (
          <button
            key={m}
            type="button"
            disabled={disabled}
            onClick={() => onMonedaChange && onMonedaChange(m)}
            style={{
              padding:    "3px 14px",
              borderRadius: 5,
              border:     `1px solid ${moneda === m ? "#f97316" : "var(--border-card)"}`,
              background: moneda === m ? "rgba(249,115,22,0.18)" : "var(--bg-input)",
              color:      moneda === m ? "#f97316" : "var(--text-secondary)",
              fontWeight: moneda === m ? 600 : 400,
              fontSize:   "0.82rem",
              cursor:     disabled ? "default" : "pointer",
              transition: "all 0.15s",
            }}
          >
            {m}
          </button>
        ))}
      </div>

      {/* ── Tipo de cambio — siempre visible ─────────────────────── */}
      <div style={{
        display: "flex", flexDirection: "column", gap: 6,
        padding: "10px 12px",
        background: "var(--bg-input, rgba(255,255,255,0.03))",
        border: "1px solid var(--border-card)",
        borderRadius: 7,
      }}>
        {/* Fila badge + label */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: "0.78rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
            Tipo de cambio (₡ / 1 USD):
          </span>
          {loadingTC ? (
            <i className="fa-solid fa-circle-notch fa-spin" style={{ color: "#60a5fa", fontSize: "0.8rem" }} />
          ) : (
            <span style={{
              fontSize: "0.72rem",
              background: `${badge.color}20`,
              border: `1px solid ${badge.color}50`,
              borderRadius: 4,
              padding: "1px 7px",
              color: badge.color,
              fontWeight: 600,
            }}>
              {badge.text}
            </span>
          )}
        </div>

        {/* Fila input TC */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>₡</span>
          <input
            type="number"
            step="0.01"
            min="0.01"
            disabled={disabled}
            value={tc}
            onChange={(e) => onTCChange && onTCChange(parseFloat(e.target.value) || 1)}
            style={{
              width: 110,
              padding: "5px 8px",
              background: "var(--bg-input)",
              border: "1px solid var(--border-card)",
              borderRadius: 6,
              color: "var(--text-primary)",
              fontSize: "0.85rem",
            }}
          />
          <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
            por 1 USD
            {moneda === "CRC" && (
              <span style={{ marginLeft: 6, color: "var(--text-muted)", fontSize: "0.7rem" }}>
                (para conversión cruzada)
              </span>
            )}
          </span>
        </div>

        {/* Equivalente en colones */}
        {montoNum > 0 && (
          <div style={{ fontSize: "0.78rem", color: "var(--text-secondary)", borderTop: "1px solid var(--border-card)", paddingTop: 6, display: "flex", justifyContent: "space-between" }}>
            <span>
              {moneda === "USD" ? (
                <><strong style={{ color: "#60a5fa" }}>{fmtUSD(montoNum)}</strong> → equivalente en colones:</>
              ) : (
                <>Monto en colones:</>
              )}
            </span>
            <strong style={{ color: "var(--text-primary)" }}>{fmtCRC(equivalente)}</strong>
          </div>
        )}
      </div>
    </div>
  );
}

export default MonedaSelector;
